import React from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Search, Clock, ArrowRight } from 'lucide-react'

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''

  // This would typically come from a search API
  const searchResults = [
    {
      title: 'First Conversation with AI',
      description: 'Step-by-step guide to having your first English conversation with AI',
      type: 'Guide',
      difficulty: 'beginner',
      readTime: '5 min read',
      url: '/guides/first-conversation-with-ai',
      relevanceScore: 95
    },
    {
      title: 'How do I start my first conversation with AI?',
      description: 'Simply choose your AI teacher, select a conversation topic, and click "Start Practice". Begin with a simple greeting...',
      type: 'FAQ',
      difficulty: null,
      readTime: null,
      url: '/faq#first-conversation',
      relevanceScore: 88
    },
    {
      title: 'Daily Conversation Practice',
      description: 'Common phrases and expressions for everyday situations',
      type: 'Guide',
      difficulty: 'beginner',
      readTime: '6 min read',
      url: '/guides/daily-conversation-practice',
      relevanceScore: 82
    },
    {
      title: 'Microphone Not Working',
      description: 'AI can\'t hear me speaking, no audio input detected, microphone permission denied',
      type: 'Troubleshooting',
      difficulty: null,
      readTime: null,
      url: '/troubleshooting#microphone',
      relevanceScore: 75
    },
    {
      title: 'Confidence Building Techniques',
      description: 'Overcome speaking anxiety and build confidence with AI practice',
      type: 'Guide',
      difficulty: 'beginner',
      readTime: '9 min read',
      url: '/guides/confidence-building-techniques',
      relevanceScore: 68
    }
  ].filter(result => 
    result.title.toLowerCase().includes(query.toLowerCase()) ||
    result.description.toLowerCase().includes(query.toLowerCase())
  )

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Guide':
        return 'bg-blue-500/20 text-blue-400'
      case 'FAQ':
        return 'bg-green-500/20 text-green-400'
      case 'Troubleshooting':
        return 'bg-orange-500/20 text-orange-400'
      default:
        return 'bg-slate-500/20 text-slate-400'
    }
  }

  const getDifficultyColor = (difficulty: string | null) => {
    if (!difficulty) return null
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/20 text-green-400'
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'advanced':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-slate-500/20 text-slate-400'
    }
  }

  const highlightText = (text: string, query: string) => {
    if (!query) return text
    const regex = new RegExp(`(${query})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">
            Search Results for "<span className="gradient-text">{query}</span>"
          </h1>
          <p className="text-slate-300">
            Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 ? (
          <div className="space-y-6">
            {searchResults.map((result, index) => (
              <Link
                key={index}
                to={result.url}
                className="group block bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getTypeColor(result.type)}`}>
                      {result.type}
                    </span>
                    {result.difficulty && (
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getDifficultyColor(result.difficulty)}`}>
                        {result.difficulty.charAt(0).toUpperCase() + result.difficulty.slice(1)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center text-slate-400">
                    <span className="text-xs mr-2">{result.relevanceScore}% match</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
                
                <h2 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {highlightText(result.title, query)}
                </h2>
                
                <p className="text-slate-300 mb-4 line-clamp-2">
                  {highlightText(result.description, query)}
                </p>
                
                {result.readTime && (
                  <div className="flex items-center text-sm text-slate-400">
                    <Clock className="h-4 w-4 mr-1" />
                    {result.readTime}
                  </div>
                )}
              </Link>
            ))}
          </div>
        ) : (
          /* No Results */
          <div className="text-center py-12">
            <Search className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-white mb-4">No results found</h2>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              We couldn't find any content matching "{query}". Try using different keywords or browse our guides directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/guides"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
              >
                Browse All Guides
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
              >
                Check FAQ
              </Link>
            </div>
          </div>
        )}

        {/* Search Tips */}
        {searchResults.length > 0 && (
          <div className="mt-12 bg-slate-800/30 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Search Tips</h3>
            <ul className="space-y-2 text-slate-300">
              <li>• Try using more specific keywords</li>
              <li>• Use quotes for exact phrases: "first conversation"</li>
              <li>• Check spelling and try alternative terms</li>
              <li>• Browse categories if you're looking for general topics</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchResultsPage